/* Sava — forms.js
   Waitlist + feedback handlers. Klaviyo client subscribe API with
   graceful fallback to Shopify /contact (form_type=contact). Vanilla, no deps.
   Uses event delegation so handler binding can't race with DOM init. */
(function () {
  'use strict';

  var VERSION = '0.5-baseline';
  var log = (window.console && console.log) ? function () {
    var args = ['[sava-forms]'];
    for (var i = 0; i < arguments.length; i++) args.push(arguments[i]);
    console.log.apply(console, args);
  } : function () {};

  log('initialized', VERSION);

  var EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var KLAVIYO_REVISION = '2024-10-15';
  var settings = window.savaSettings || {};
  var strings = window.savaStrings || {};

  log('settings snapshot', {
    klaviyoKey: settings.klaviyoKey,
    waitlistListId: settings.waitlistListId,
    feedbackListId: settings.feedbackListId,
    notifyListId: settings.notifyListId,
    locale: settings.locale
  });

  // Schema defaults are placeholder strings ("your-…-here") so Shopify accepts
  // upload before real keys are configured. Treat them as unconfigured.
  function configured(v) {
    return !!v && typeof v === 'string' && !/^your-.*-here$/.test(v);
  }

  function findFeedback(form) {
    var el = form.querySelector('[data-form-feedback]');
    if (el) return el;
    var field = form.querySelector('input[type="email"], textarea');
    var id = field && field.getAttribute('aria-describedby');
    return id ? document.getElementById(id) : null;
  }

  function setFeedback(form, type, text) {
    var el = findFeedback(form);
    if (!el) return;
    el.textContent = text || '';
    var classes = (el.className || '').split(/\s+/).filter(Boolean);
    var base = '';
    for (var i = 0; i < classes.length; i++) {
      if (classes[i].indexOf('--') === -1) base = classes[i];
      else el.classList.remove(classes[i]);
    }
    if (type && base) el.classList.add(base + '--' + type);
  }

  function disable(form, on) {
    var btn = form.querySelector('button[type="submit"]');
    if (btn) {
      btn.disabled = !!on;
      if (on) btn.setAttribute('aria-busy', 'true');
      else btn.removeAttribute('aria-busy');
    }
    if (on) form.setAttribute('aria-busy', 'true');
    else form.removeAttribute('aria-busy');
  }

  function postKlaviyo(kind, listId, email, properties) {
    var url = 'https://a.klaviyo.com/client/subscriptions/?company_id=' +
      encodeURIComponent(settings.klaviyoKey);
    var profileAttrs = { email: email };
    if (properties) profileAttrs.properties = properties;
    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'sava-coming-soon',
          profile: { data: { type: 'profile', attributes: profileAttrs } }
        },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    };
    log(kind + ' fetch firing', { url: url, listId: listId, email: email, hasProperties: !!properties });
    return fetch(url, {
      method: 'POST',
      headers: { revision: KLAVIYO_REVISION, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      mode: 'cors'
    }).then(function (r) {
      log(kind + ' klaviyo response', r.status);
      if (!r.ok && r.status !== 202) throw new Error('klaviyo ' + r.status);
      return r;
    });
  }

  function postShopifyContact(form) {
    return fetch(form.action, {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'text/html' },
      credentials: 'same-origin'
    }).then(function (r) {
      if (!r.ok) throw new Error('shopify ' + r.status);
      return r;
    });
  }

  function announceSuccess(form, key) {
    log('announceSuccess', key);
    // Optional {product} token: substituted from the form's data-notify-product
    // attribute (set by sava-product-object). Strings without the token are
    // unchanged. split/join keeps the value a pure literal (no $-pattern quirks).
    var msg = strings[key] || '';
    if (msg.indexOf('{product}') !== -1) {
      var product = form.getAttribute('data-notify-product') || '';
      msg = msg.split('{product}').join(product);
    }
    setFeedback(form, 'success', msg);
    form.reset();
    // Clear aria-invalid on every input that might have been marked,
    // not just the first one — the feedback form has two fields.
    var marked = form.querySelectorAll('[aria-invalid="true"]');
    for (var i = 0; i < marked.length; i++) marked[i].removeAttribute('aria-invalid');
  }

  function announceError(form) {
    setFeedback(form, 'error', strings.errorGeneric || '');
  }

  function handleSubmit(form, kind, e) {
    e.preventDefault();

    var emailEl = form.querySelector('input[type="email"]');
    var bodyEl = form.querySelector('textarea');
    var email = emailEl ? emailEl.value.trim() : '';
    var feedback = bodyEl ? bodyEl.value.trim() : '';

    if (kind === 'feedback' && !feedback) {
      if (bodyEl) bodyEl.setAttribute('aria-invalid', 'true');
      setFeedback(form, 'error', strings.errorInvalid);
      return;
    }
    if (kind === 'waitlist' && !EMAIL_RX.test(email)) {
      if (emailEl) emailEl.setAttribute('aria-invalid', 'true');
      setFeedback(form, 'error', strings.errorInvalid);
      return;
    }
    if (kind === 'notify' && !EMAIL_RX.test(email)) {
      if (emailEl) emailEl.setAttribute('aria-invalid', 'true');
      setFeedback(form, 'error', strings.errorInvalid);
      return;
    }
    if (kind === 'feedback' && email && !EMAIL_RX.test(email)) {
      if (emailEl) emailEl.setAttribute('aria-invalid', 'true');
      setFeedback(form, 'error', strings.errorInvalid);
      return;
    }
    if (emailEl) emailEl.removeAttribute('aria-invalid');
    if (bodyEl) bodyEl.removeAttribute('aria-invalid');

    disable(form, true);
    setFeedback(form, null, '');

    var successKey = kind === 'waitlist'
      ? 'successWaitlist'
      : kind === 'notify' ? 'successNotify' : 'successFeedback';

    var listId;
    if (kind === 'waitlist') {
      listId = settings.waitlistListId;
    } else if (kind === 'notify') {
      // Notify falls back to the waitlist list id when a dedicated notify list
      // is not yet configured, so per-SKU capture still works pre-launch.
      listId = settings.notifyListId || settings.waitlistListId;
      if (!configured(settings.notifyListId) && configured(settings.waitlistListId)) {
        log('notify list id unconfigured — falling back to waitlistListId');
      }
    } else {
      listId = settings.feedbackListId;
    }

    var properties = null;
    if (kind === 'feedback' && feedback) {
      properties = { feedback_text: feedback };
    } else if (kind === 'notify') {
      var notifyProduct = form.getAttribute('data-notify-product') || '';
      var notifyHandle  = form.getAttribute('data-notify-handle')  || '';
      properties = { notify_product: notifyProduct, notify_handle: notifyHandle };
    }

    var done = function () {
      disable(form, false);
      announceSuccess(form, successKey);
    };
    var failHard = function () {
      disable(form, false);
      announceError(form);
    };
    var fallback = function () {
      postShopifyContact(form).then(done, failHard);
    };

    if (configured(settings.klaviyoKey) && configured(listId) && email) {
      log(kind + ' → Klaviyo path');
      postKlaviyo(kind, listId, email, properties).then(done, fallback);
    } else {
      log(kind + ' → /contact fallback path');
      fallback();
    }
  }

  // Event delegation: catches submits even if the form gets re-rendered or
  // if this script ran before the form existed in the DOM.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    var kind = form.getAttribute('data-form');
    if (kind !== 'waitlist' && kind !== 'feedback' && kind !== 'notify') return;
    log('submit intercepted', kind);
    try {
      handleSubmit(form, kind, e);
    } catch (err) {
      e.preventDefault();
      disable(form, false);
      announceError(form);
      if (window.console && console.error) console.error('[sava-forms]', err);
    }
  }, false);

  log('submit listener attached on document');
})();
